import {createSelector} from 'reselect';

import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {AppParametricSelector, AppSelector} from '../../../helpers/reselectHelpers';
import {getHasSupportChatFeature} from '../../data/company/companySelectors';
import {getHelpChatConfig} from '../../data/dataSelectors';
import {getIsChatWidgetUnavailable} from '../../featureFlags/featureFlagsSelectors';
import {makeGetAnalyticsPath} from '../analytics/analyticsPanelSelectors';
import {makeGetContactsManagerPath} from '../contacts/contactsManager/contactsManagerPanelSelectors';
import {makeGetInboxesPath} from '../inboxes/inboxesPanelSelectors';
import {makeGetLegacyPath} from '../legacy/legacyPanelSelectors';
import {getPanels, makeGetAppPanelIdFromPanel} from '../panelsSelectors';
import {
  AppPanelId,
  makeAnalyticsPanelId,
  makeContactsManagerPanelId,
  makeInboxesPanelId,
  makeLegacyPanelId,
  PanelTypesEnum,
} from '../panelStateBase';
import {AppPanelState} from './appPanelState';
import {AppContentTypesEnum, defaultPathsByContentType} from './parameters/appParameters';

/*
 * Panel selectors.
 */

export interface AppPanelProps {
  panelId: AppPanelId;
}

export type AppPanelIdSelector<P> = AppParametricSelector<P, AppPanelId>;

function getAppPanelId(_state: unknown, props: AppPanelProps) {
  return props.panelId;
}

export type AppPanelSelector<P> = AppParametricSelector<P, AppPanelState>;

export function makeGetAppPanel(): AppPanelSelector<AppPanelProps>;
export function makeGetAppPanel<P>(getPanelId: AppPanelIdSelector<P>): AppPanelSelector<P>;
export function makeGetAppPanel(getPanelId: AppPanelIdSelector<any> = getAppPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    if (!panel || panel.type !== PanelTypesEnum.APP) {
      throw new Error('App panel not found.');
    }

    return panel;
  });
}

export function makeGetAppPath(): AppParametricSelector<AppPanelProps, string>;
export function makeGetAppPath<P>(getPanelId: AppPanelIdSelector<P>): AppParametricSelector<P, string>;
export function makeGetAppPath(
  getPanelId: AppPanelIdSelector<any> = getAppPanelId,
): AppParametricSelector<any, string> {
  const getAppPanel = makeGetAppPanel(getPanelId);

  const getGetContentPath = createSelector(getAppPanel, (panel): AppSelector<string> => {
    switch (panel.contentType) {
      case AppContentTypesEnum.INBOXES: {
        const panelId = makeInboxesPanelId(panel.id);
        const getInboxesPath = makeGetInboxesPath();
        return (state) => getInboxesPath(state, {panelId});
      }

      case AppContentTypesEnum.CALENDARS:
      case AppContentTypesEnum.VISITOR_VIEWER:
      case AppContentTypesEnum.VISITOR_EXPERIENCE:
      case AppContentTypesEnum.VISITOR_EXPERIENCE_V3:
      case AppContentTypesEnum.STANDALONE_COMPOSER:
        return () => defaultPathsByContentType[panel.contentType];

      case AppContentTypesEnum.CONTACTS: {
        const panelId = makeContactsManagerPanelId(panel.id);
        const getContactsManagerPath = makeGetContactsManagerPath();
        return (state) => getContactsManagerPath(state, {panelId});
      }

      case AppContentTypesEnum.ANALYTICS: {
        const panelId = makeAnalyticsPanelId(panel.id);
        const getAnalyticsPath = makeGetAnalyticsPath();
        return (state) => getAnalyticsPath(state, {panelId});
      }

      case AppContentTypesEnum.SEQUENCES:
      case AppContentTypesEnum.AUDIT_TRAIL:
      case AppContentTypesEnum.SETTINGS: {
        const panelId = makeLegacyPanelId(panel.id);
        const getLegacyPath = makeGetLegacyPath();
        return (state) => getLegacyPath(state, {panelId});
      }

      default:
        throw new UnreachableError(panel.contentType);
    }
  });

  return (state, props) => getGetContentPath(state, props)(state);
}

export function makeGetBlockedNavigation() {
  const getAppPanelIdFromPanel = makeGetAppPanelIdFromPanel();
  const getAppPanel = makeGetAppPanel(getAppPanelIdFromPanel);

  return createSelector(getAppPanel, (panel) => panel.blockedNavigation);
}

/*
 * Help chat.
 */

export const getActiveHelpChatConfig = createSelector(
  [getHelpChatConfig, getIsChatWidgetUnavailable, getHasSupportChatFeature],
  (helpChatConfig, isChatWidgetUnavailable, hasSupportChatFeature) => {
    if (!helpChatConfig || !helpChatConfig.isEnabled) {
      return undefined;
    }

    if (isChatWidgetUnavailable || !hasSupportChatFeature) {
      return undefined;
    }

    return helpChatConfig;
  },
);

/*
 * Dialogs and Popovers.
 */

export function makeGetIsDemoInboxLoadingModalOpen() {
  return createSelector(makeGetAppPanel(), (panel) => panel.isDemoInboxLoadingModalOpen);
}

export function makeGetInviteTeammatePopoverOpen() {
  return createSelector(makeGetAppPanel(), (panel) => panel.inviteTeammatePopover);
}
